/**
 * Sokak haritası saf mantığı (P6): GeoJSON ayrıştırma, alan seçimi geometrisi, seçim → kural
 * gövdesi, atama → renk eşlemesi, adres deneme durumu. Leaflet'e ve React'e bağımlı DEĞİL;
 * ekran (`StreetMap.tsx`) yalnız bunu çağırır.
 *
 * Eşleme kuralı sunucudadır (`regions/match-street.ts`); panel hiçbir adres eşlemesi yapmaz.
 * Burada yalnız seçilen çizgilerin AYNI ADA göre gruplanması vardır — sunucu zaten kanonik adla
 * birleştirdiği için istemci tarafı gruplama yalnız gereksiz kural sayısını azaltır.
 */
import {
  foldTurkish,
  t,
  type MessageKey,
  vehicleColor,
  vehicleColors as fleetColors,
  VEHICLE_COLOR_PALETTE,
} from '@aquabox/shared';
import { ApiError } from '../api/client';
import type { BulkRulesBody, StreetMatchResult, StreetRule, StreetSide } from '../api/street-rules';

/** [enlem, boylam] — Leaflet sırası; GeoJSON'daki [boylam, enlem] okunurken çevrilir. */
export type LatLngTuple = [number, number];

export interface StreetLine {
  osmId: number;
  name: string | null;
  highway: string;
  mahalle: string | null;
  /** MultiLineString'e karşı: her parça ayrı bir dizi. */
  parts: LatLngTuple[][];
}

interface RawFeature {
  properties?: {
    osmId?: number;
    name?: string | null;
    highway?: string;
    mahalle?: string | null;
  } | null;
  geometry?: { type?: string; coordinates?: unknown } | null;
}

const flip = (c: number[]): LatLngTuple => [c[1] ?? 0, c[0] ?? 0];

/** Sokak GeoJSON'unu düz çizgi listesine çevirir; bozuk kayıtları atlar. */
export function parseStreetGeoJson(fc: { features?: RawFeature[] }): StreetLine[] {
  const out: StreetLine[] = [];
  for (const f of fc.features ?? []) {
    const p = f.properties;
    const g = f.geometry;
    if (!p || typeof p.osmId !== 'number' || !g) continue;
    let parts: LatLngTuple[][];
    if (g.type === 'LineString') {
      parts = [(g.coordinates as number[][]).map(flip)];
    } else if (g.type === 'MultiLineString') {
      parts = (g.coordinates as number[][][]).map((part) => part.map(flip));
    } else continue;
    if (parts.every((part) => part.length < 2)) continue;
    out.push({
      osmId: p.osmId,
      name: p.name && p.name.trim() ? p.name.trim() : null,
      highway: p.highway ?? '',
      mahalle: p.mahalle && p.mahalle.trim() ? p.mahalle.trim() : null,
      parts,
    });
  }
  return out;
}

/** Ad karşılaştırma anahtarı: boşluk tekil, Türkçe küçük harf, ASCII kıvrım ("cikinlar" = "Çıkınlar"). */
export const nameKey = (s: string): string => foldTurkish(s);

// ── Alan seçimi geometrisi ──────────────────────────────────────────────────────────

/** Işın atma: nokta çokgenin içinde mi? */
export function pointInPolygon(pt: LatLngTuple, ring: readonly LatLngTuple[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [yi, xi] = ring[i]!;
    const [yj, xj] = ring[j]!;
    const crosses =
      yi > pt[0] !== yj > pt[0] && pt[1] < ((xj - xi) * (pt[0] - yi)) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

function segmentsIntersect(
  a: LatLngTuple,
  b: LatLngTuple,
  c: LatLngTuple,
  d: LatLngTuple,
): boolean {
  const cross = (o: LatLngTuple, p: LatLngTuple, q: LatLngTuple): number =>
    (p[1] - o[1]) * (q[0] - o[0]) - (p[0] - o[0]) * (q[1] - o[1]);
  const d1 = cross(a, b, c);
  const d2 = cross(a, b, d);
  const d3 = cross(c, d, a);
  const d4 = cross(c, d, b);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

/** Köşeleri verilen iki noktadan dikdörtgen halkası. */
export function rectToRing(a: LatLngTuple, b: LatLngTuple): LatLngTuple[] {
  return [
    [a[0], a[1]],
    [a[0], b[1]],
    [b[0], b[1]],
    [b[0], a[1]],
  ];
}

function ringBounds(ring: readonly LatLngTuple[]): [number, number, number, number] {
  let s = Infinity;
  let w = Infinity;
  let n = -Infinity;
  let e = -Infinity;
  for (const [lat, lng] of ring) {
    s = Math.min(s, lat);
    n = Math.max(n, lat);
    w = Math.min(w, lng);
    e = Math.max(e, lng);
  }
  return [s, w, n, e];
}

/** Çizginin herhangi bir noktası çokgenin içindeyse ya da bir kenarı çokgeni kesiyorsa true. */
export function lineTouchesPolygon(line: StreetLine, ring: readonly LatLngTuple[]): boolean {
  if (ring.length < 3) return false;
  const [s, w, n, e] = ringBounds(ring);
  for (const part of line.parts) {
    for (let i = 0; i < part.length; i++) {
      const p = part[i]!;
      if (p[0] >= s && p[0] <= n && p[1] >= w && p[1] <= e && pointInPolygon(p, ring)) return true;
      const q = part[i + 1];
      if (!q) continue;
      for (let k = 0; k < ring.length; k++) {
        if (segmentsIntersect(p, q, ring[k]!, ring[(k + 1) % ring.length]!)) return true;
      }
    }
  }
  return false;
}

export function linesInPolygon(
  lines: readonly StreetLine[],
  ring: readonly LatLngTuple[],
): number[] {
  return lines.filter((l) => lineTouchesPolygon(l, ring)).map((l) => l.osmId);
}

// ── Seçim → kural gövdesi ───────────────────────────────────────────────────────────

export interface Target {
  kind: 'vehicle' | 'region';
  id: string;
}

/** Hedef seçimi `"v:<id>"` / `"r:<id>"` olarak tutulur (tek `<select>`). */
export function parseTarget(value: string): Target | null {
  const m = /^([vr]):(.+)$/.exec(value);
  if (!m) return null;
  return { kind: m[1] === 'v' ? 'vehicle' : 'region', id: m[2]! };
}

export const targetValue = (target: Target): string =>
  `${target.kind === 'vehicle' ? 'v' : 'r'}:${target.id}`;

/** Sunucu istek başına en çok bu kadar kural kabul eder. */
export const BULK_LIMIT = 500;

export interface BuiltRules {
  bodies: BulkRulesBody[];
  /** Kural olacak sokak sayısı (ad + mahalle çifti). */
  ruleCount: number;
  /** Adı olmadığı için atlanan çizgi sayısı. */
  skippedNoName: number;
  /** Mahallesi olmadığı için atlanan çizgi sayısı. */
  skippedNoNeighborhood: number;
}

/**
 * Seçilen çizgilerden sunucu gövdelerini kurar.
 *
 *  - Adı OSM'de olan çizgi adını korur; adsız çizgilere `nameInput` (patronun yazdığı) verilir.
 *  - Aynı mahalle + aynı ad tek kural olur, `osmWayIds` birleşir; farklı adlar ayrı kuraldır.
 *  - `all` verilirse, seçilen ADLI çizgilerle aynı mahalle ve aynı adı taşıyan tüm parçalar da
 *    kurala eklenir (bir sokak OSM'de çok parçaya bölünür; patron hepsini tıklamak zorunda kalmaz).
 *  - Mahallesi ya da adı olmayan çizgi atlanır ve sayılır; ekran bunu uyarı olarak gösterir.
 *  - 500'den fazla kural birden çok gövdeye bölünür.
 */
export function buildRuleBodies(
  selected: readonly StreetLine[],
  target: Target,
  nameInput: string,
  all?: readonly StreetLine[],
): BuiltRules {
  const typed = nameInput.trim().replace(/\s+/g, ' ');
  const lines = new Map<number, StreetLine>(selected.map((l) => [l.osmId, l]));

  if (all) {
    const wanted = new Set(
      selected
        .filter((l) => l.name && l.mahalle)
        .map((l) => `${nameKey(l.mahalle!)}|${nameKey(l.name!)}`),
    );
    for (const l of all) {
      if (l.name && l.mahalle && wanted.has(`${nameKey(l.mahalle)}|${nameKey(l.name)}`)) {
        lines.set(l.osmId, l);
      }
    }
  }

  let skippedNoName = 0;
  let skippedNoNeighborhood = 0;
  const groups = new Map<string, { neighborhood: string; street: string; ways: Set<string> }>();
  for (const l of lines.values()) {
    if (!l.mahalle) {
      skippedNoNeighborhood++;
      continue;
    }
    const street = l.name ?? typed;
    if (!street) {
      skippedNoName++;
      continue;
    }
    const key = `${nameKey(l.mahalle)}|${nameKey(street)}`;
    const g = groups.get(key) ?? { neighborhood: l.mahalle, street, ways: new Set<string>() };
    g.ways.add(String(l.osmId));
    groups.set(key, g);
  }

  const rules = [...groups.values()].map((g) => ({
    neighborhood: g.neighborhood,
    street: g.street,
    osmWayIds: [...g.ways].sort((a, b) => Number(a) - Number(b)),
  }));
  const targetKey = target.kind === 'vehicle' ? { vehicleId: target.id } : { regionId: target.id };
  const bodies: BulkRulesBody[] = [];
  for (let i = 0; i < rules.length; i += BULK_LIMIT) {
    bodies.push({ ...targetKey, rules: rules.slice(i, i + BULK_LIMIT) });
  }
  return { bodies, ruleCount: rules.length, skippedNoName, skippedNoNeighborhood };
}

/** Yan paneldeki seçim özeti. */
export function summarizeSelection(selected: readonly StreetLine[], nameInput: string) {
  const neighborhoods = [
    ...new Set(selected.map((l) => l.mahalle).filter((m): m is string => !!m)),
  ];
  const unnamed = selected.filter((l) => !l.name).length;
  return {
    lines: selected.length,
    neighborhoods,
    unnamed,
    /** Adsız çizgi var ve patron henüz ad yazmadı: Kaydet kapalı kalır. */
    needsName: unnamed > 0 && nameInput.trim() === '',
    names: [...new Set(selected.map((l) => l.name).filter((n): n is string => !!n))],
  };
}

// ── Atama → renk ────────────────────────────────────────────────────────────────────

export const UNASSIGNED_COLOR = '#9AA0A6';
export const SELECTED_COLOR = '#E8590C';
export const UNASSIGNED_REGION_COLOR = '#7B8794';
/** Bölgesiz mahalle çizgisi (dolgudan koyu, kesikli çizgide okunur). */
export const UNASSIGNED_OUTLINE_COLOR = '#6B7280';

/**
 * Araç rengi — paylaşılan FNV-1a 32-bit paletinden (`@aquabox/shared/vehicle-color`). Plaka
 * sabit olduğu için ARAÇ RENGİ KARARLIDIR: yeni araç eklendiğinde diğerlerinin rengi kaymaz.
 * `vehicleColor('34 ABC 123')` her zaman aynı hex değerini verir.
 */
export { vehicleColor };

/**
 * Tüm aktif araçlar için id → renk eşlemesi. `vehicleColor` paylaşılan paletinden gelir;
 * filo listesiyle çakışmasız dağıtılır (paylaşılan vehicleColors); yeni araç eklenince yalnız çakışan araçların rengi değişebilir.
 */
export function vehicleColors(
  vehicles: readonly { id: string; plate: string }[],
): Map<string, string> {
  const byPlate = fleetColors(vehicles.map((v) => v.plate));
  return new Map(vehicles.map((v) => [v.id, byPlate.get(v.plate) ?? vehicleColor(v.plate)]));
}

export interface Assignment {
  ruleId: string;
  color: string;
  label: string;
  target: Target;
  /** Aynı sokağın parçaları farklı hedefte: harita "bölünmüş" stille boyar. */
  split?: boolean;
}

export function targetLabel(
  target: Target,
  vehicles: readonly { id: string; plate: string }[],
  regions: readonly { id: string; name: string }[],
): string {
  if (target.kind === 'vehicle') {
    const v = vehicles.find((x) => x.id === target.id);
    return t('streetMap.target.vehicle', { plate: v?.plate ?? '?' });
  }
  const r = regions.find((x) => x.id === target.id);
  return t('streetMap.target.region', { name: r?.name ?? '?' });
}

const isAllSegment = (r: StreetRule): boolean =>
  (r.side ?? 'ALL') === 'ALL' && r.numberFrom == null && r.numberTo == null;
const targetKeyOf = (r: StreetRule): string =>
  r.vehicleId ? `v:${r.vehicleId}` : `r:${r.regionId ?? ''}`;
const groupKeyOf = (neighborhood: string, street: string): string =>
  `${nameKey(neighborhood)}|${nameKey(street)}`;

/**
 * OSM yol kimliği → atama. Araç hedefi araç rengiyle, bölge hedefi bölgenin rengiyle boyanır
 * (bölgenin rengi yoksa gri-mavi). Pasif kural ve hedefi bilinmeyen kural boyanmaz.
 *
 * Bölünmüş sokak: aynı (mahalle, sokak) grubunun aktif parçalarının yol kimlikleri birleşir.
 * Parçalar farklı hedefteyse tüm yollar `split` işaretlenir; renk/kural ALL satırından (yoksa
 * ilk parçadan) gelir.
 */
export function buildAssignmentIndex(
  rules: readonly StreetRule[],
  vehicles: readonly { id: string; plate: string }[],
  regions: readonly { id: string; name: string; color: string | null }[],
): Map<string, Assignment> {
  const colors = vehicleColors(vehicles);
  const groups = new Map<string, StreetRule[]>();
  for (const rule of rules) {
    if (!rule.isActive) continue;
    const key = groupKeyOf(rule.neighborhood, rule.street);
    const g = groups.get(key);
    if (g) g.push(rule);
    else groups.set(key, [rule]);
  }
  const out = new Map<string, Assignment>();
  for (const group of groups.values()) {
    const head = group.find(isAllSegment) ?? group[0]!;
    let target: Target;
    let color: string | undefined;
    if (head.vehicleId) {
      target = { kind: 'vehicle', id: head.vehicleId };
      color = colors.get(head.vehicleId);
    } else if (head.regionId) {
      target = { kind: 'region', id: head.regionId };
      color = regions.find((r) => r.id === head.regionId)?.color ?? '#4C6A92';
    } else continue;
    if (!color) continue;
    const label = targetLabel(target, vehicles, regions);
    const split = new Set(group.map(targetKeyOf)).size > 1;
    const ways = new Set(group.flatMap((r) => r.osmWayIds));
    for (const way of ways) {
      out.set(way, { ruleId: head.id, color, label, target, ...(split ? { split } : {}) });
    }
  }
  return out;
}

/** Seçili çizgilerden bölünmüş sokağa ait olanların adları (haritadan atama uyarısı için). */
export function splitStreetNames(
  selected: readonly StreetLine[],
  index: ReadonlyMap<string, Assignment>,
): string[] {
  return [
    ...new Set(
      selected
        .filter((l) => index.get(String(l.osmId))?.split)
        .map((l) => l.name ?? t('streetMap.unnamed')),
    ),
  ];
}

// ── Sokak parçaları (yan + kapı no aralığı) ────────────────────────────────────────

const DOOR_MIN = 1;
const DOOR_MAX = 9999;

/** Parça etiketi: "Tümü", "Tek · 1–99", "Çift · 100–", "Tümü · –50". */
export function segmentLabel(
  side: StreetSide | undefined,
  from: number | null | undefined,
  to: number | null | undefined,
): string {
  const sideText = t(`regions.split.side.${side ?? 'ALL'}` as MessageKey);
  if (from == null && to == null) return sideText;
  return `${sideText} · ${from ?? ''}–${to ?? ''}`;
}

/** Bir sokağın (mahalle + kanonik ad) aktif parçaları; ALL satırı başta, sonra kapı no sırası. */
export function streetSegments(
  rules: readonly StreetRule[],
  neighborhood: string,
  street: string,
): StreetRule[] {
  const key = groupKeyOf(neighborhood, street);
  return rules
    .filter((r) => r.isActive && groupKeyOf(r.neighborhood, r.street) === key)
    .sort(
      (x, y) =>
        Number(isAllSegment(y)) - Number(isAllSegment(x)) ||
        (x.numberFrom ?? 0) - (y.numberFrom ?? 0),
    );
}

export type SegmentInput =
  | { ok: true; side: StreetSide; numberFrom?: number; numberTo?: number }
  | { ok: false; error: string };

/** Formdaki taraf + kapı no uçlarını doğrular. Boş uç sınırsız; Tümü + aralıksız bölme sayılmaz. */
export function parseSegmentInput(
  side: StreetSide,
  fromText: string,
  toText: string,
): SegmentInput {
  const parse = (v: string): number | null | undefined => {
    const x = v.trim();
    if (x === '') return undefined;
    if (!/^[0-9]{1,4}$/.test(x)) return null;
    const n = Number(x);
    return n >= DOOR_MIN && n <= DOOR_MAX ? n : null;
  };
  const from = parse(fromText);
  const to = parse(toText);
  if (from === null || to === null) return { ok: false, error: t('regions.split.errNumber') };
  if (from !== undefined && to !== undefined && from > to) {
    return { ok: false, error: t('regions.split.errOrder') };
  }
  if (side === 'ALL' && from === undefined && to === undefined) {
    return { ok: false, error: t('regions.split.errNeedsRange') };
  }
  return {
    ok: true,
    side,
    ...(from !== undefined ? { numberFrom: from } : {}),
    ...(to !== undefined ? { numberTo: to } : {}),
  };
}

export const colorForLine = (osmId: number, index: ReadonlyMap<string, Assignment>): string =>
  index.get(String(osmId))?.color ?? UNASSIGNED_COLOR;

// ── Mahalle → bölge (P6 sadeleştirme) ──────────────────────────────────────────────

export interface RegionForNeighborhood {
  id: string;
  name: string;
  color: string | null;
  /** Mahallenin kanonik adı → bölge. Aynı ada iki bölge varsa İLKİ kazanır (sunucu zaten reddeder). */
  neighborhoods: string[];
  defaultVehicle: { id: string; plate: string } | null;
}

/**
 * Mahalle adı (kanonik, küçük harf + Türkçe kıvrımsız) → bölge. Eşleşme yoksa undefined.
 * Mahallenin panelden gelen adı küçük harfe indirgenmiş olarak gelir (`regions.schema.ts`);
 * `nameKey` ile aynı normalleştirmeyi uygularız.
 */
export function buildNeighborhoodRegionIndex(
  regions: readonly RegionForNeighborhood[],
): Map<string, RegionForNeighborhood> {
  const out = new Map<string, RegionForNeighborhood>();
  for (const r of regions) {
    for (const n of r.neighborhoods) {
      const k = nameKey(n);
      if (!out.has(k)) out.set(k, r);
    }
  }
  return out;
}

/**
 * Mahallenin bölge rengini döner; atanmamışsa varsayılan nötr renk. Üst lejantta ve haritadaki
 * mahalle poligonunda aynı değer kullanılır.
 */
export function neighborhoodColor(
  neighborhood: string,
  index: ReadonlyMap<string, RegionForNeighborhood>,
): string {
  const r = index.get(nameKey(neighborhood));
  return r?.color ?? UNASSIGNED_REGION_COLOR;
}

/**
 * Bölge palette (sunucu `regions.service.ts#nextPaletteColor` ile aynı dizi). Yeni bölge
 * için sıradaki boş renk, renksiz bölge için de sıraya göre renk. Haritada "gri bölge" görünmesin.
 */
export const REGION_PALETTE: readonly string[] = VEHICLE_COLOR_PALETTE;

/** Mahalle palet rengini bölge sırasına göre verir (regions service ile aynı mantık). */
export function paletteColorForOrder(order: number): string {
  return REGION_PALETTE[order % REGION_PALETTE.length]!;
}

/**
 * Mahalle poligonunun Leaflet stil nesnesi (boyama modunda görsel vurgu):
 *   - Bölgeli mahalle: dolgulu (0.45), düz çizgi, weight 2. Hover'da dolgu 0.6.
 *   - Bölgesiz mahalle: açık gri, kesikli, dolgu 0.15. Bölgenin sırasına göre paletten renk;
 *     sunucu renksiz bölge bırakırsa ilgili palet rengi düşer (gri düşmez).
 */
export function neighborhoodPolygonStyle(
  neighborhood: string,
  index: ReadonlyMap<string, RegionForNeighborhood>,
  regionOrder: ReadonlyMap<string, number>,
  isHover: boolean,
): {
  color: string;
  weight: number;
  opacity?: number;
  dashArray?: string;
  fillColor: string;
  fillOpacity: number;
} {
  const region = index.get(nameKey(neighborhood));
  if (region) {
    const c = region.color ?? paletteColorForOrder(regionOrder.get(region.id) ?? 0);
    return {
      color: c,
      weight: 2,
      fillColor: c,
      fillOpacity: isHover ? 0.6 : 0.45,
    };
  }
  return {
    color: UNASSIGNED_OUTLINE_COLOR,
    weight: 1.5,
    opacity: 0.8,
    dashArray: '4 4',
    fillColor: UNASSIGNED_REGION_COLOR,
    fillOpacity: isHover ? 0.2 : 0.08,
  };
}

// ── Arama ───────────────────────────────────────────────────────────────────────────

export interface SearchHit {
  key: string;
  name: string;
  mahalle: string | null;
  osmIds: number[];
}

/** Sokak adı ya da mahalle içinde arar; aynı mahalle + ad tek sonuç olur. */
export function searchStreets(
  lines: readonly StreetLine[],
  query: string,
  limit = 30,
): SearchHit[] {
  const q = nameKey(query);
  if (q.length < 2) return [];
  const hits = new Map<string, SearchHit>();
  for (const l of lines) {
    if (!l.name) continue;
    const hay = `${nameKey(l.name)} ${l.mahalle ? nameKey(l.mahalle) : ''}`;
    if (!hay.includes(q)) continue;
    const key = `${l.mahalle ? nameKey(l.mahalle) : ''}|${nameKey(l.name)}`;
    const hit = hits.get(key) ?? { key, name: l.name, mahalle: l.mahalle, osmIds: [] };
    hit.osmIds.push(l.osmId);
    hits.set(key, hit);
  }
  return [...hits.values()]
    .sort(
      (a, b) =>
        a.name.localeCompare(b.name, 'tr-TR') ||
        (a.mahalle ?? '').localeCompare(b.mahalle ?? '', 'tr-TR'),
    )
    .slice(0, limit);
}

/** Mahalle adı araması: GeoJSON'daki mahalleler arasında içerir. Türkçe karakter duyarsız. */
export interface HoodHit {
  key: string;
  name: string;
}

export function searchNeighborhoods(
  hoods: readonly { name?: string }[],
  query: string,
  limit = 12,
): HoodHit[] {
  const q = nameKey(query);
  if (q.length < 2) return [];
  const hits = new Map<string, HoodHit>();
  for (const f of hoods) {
    const name = f.name?.trim();
    if (!name) continue;
    if (!nameKey(name).includes(q)) continue;
    const key = nameKey(name);
    if (!hits.has(key)) hits.set(key, { key, name });
  }
  return [...hits.values()].sort((a, b) => a.name.localeCompare(b.name, 'tr-TR')).slice(0, limit);
}

// ── Adres deneme durumu ─────────────────────────────────────────────────────────────

export interface MatchDescription {
  /** ok: araca düşer · manual: patron elle atar · none: hiçbir şeyle eşleşmedi */
  tone: 'ok' | 'manual' | 'none';
  text: string;
}

export function describeMatch(m: StreetMatchResult): MatchDescription {
  if (m.via === 'STREET_VEHICLE' && m.vehicle) {
    return { tone: 'ok', text: t('streetMap.matcher.streetVehicle', { plate: m.vehicle.plate }) };
  }
  if (m.via === 'STREET_REGION' && m.region) {
    return m.vehicle
      ? {
          tone: 'ok',
          text: t('streetMap.matcher.streetRegion', {
            region: m.region.name,
            plate: m.vehicle.plate,
          }),
        }
      : {
          tone: 'manual',
          text: t('streetMap.matcher.streetRegionNoVehicle', { region: m.region.name }),
        };
  }
  if (m.via === 'NEIGHBORHOOD' && m.region) {
    return m.vehicle
      ? {
          tone: 'ok',
          text: t('streetMap.matcher.neighborhood', {
            region: m.region.name,
            plate: m.vehicle.plate,
          }),
        }
      : {
          tone: 'manual',
          text: t('streetMap.matcher.neighborhoodNoVehicle', { region: m.region.name }),
        };
  }
  // via null: ya hiçbir şey tutmadı ya da sokak isabetleri belirsiz.
  if (m.region || m.streetRule) return { tone: 'manual', text: t('streetMap.matcher.manual') };
  return { tone: 'none', text: t('streetMap.matcher.unmatched') };
}

/**
 * Sunucunun sakladığı mahalle biçimi: tr-TR küçük harf, Türkçe karakterler KORUNUR
 * (`regions.schema.ts`). `matchRegion` adresi katlamadan karşılaştırır; bu yüzden panel bölgeye
 * ASCII'ye katlanmış ("cikinlar") ad GÖNDERMEZ — yoksa "Çıkınlar Mah." adresi hiç eşleşmez.
 */
export function storedNeighborhoodName(name: string): string {
  return name.trim().toLocaleLowerCase('tr-TR');
}

/**
 * Mahalleyi bir bölgeden diğerine taşımanın PATCH gövdeleri. Karşılaştırma katlamalıdır
 * (`nameKey`), yazılan değer katlanmaz. `from`/`to` null ise o bölgeye PATCH atılmaz.
 */
export function buildNeighborhoodMove(args: {
  neighborhood: string;
  fromNeighborhoods: readonly string[] | null;
  toNeighborhoods: readonly string[] | null;
}): { from: string[] | null; to: string[] | null } {
  const key = nameKey(args.neighborhood);
  const stored = storedNeighborhoodName(args.neighborhood);
  const from = args.fromNeighborhoods
    ? args.fromNeighborhoods.filter((n) => nameKey(n) !== key)
    : null;
  const to = args.toNeighborhoods
    ? [...args.toNeighborhoods.filter((n) => nameKey(n) !== key), stored]
    : null;
  return { from, to };
}

/** "Böl" kaydı hata mesajı: 409 = aynı yan/aralıkta başka aktif parça var. */
export function splitSaveErrorMessage(err: unknown): string {
  return err instanceof ApiError && err.status === 409
    ? t('regions.split.errConflict')
    : t('regions.split.errSave');
}
